import { useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from '@mui/material';
import { StyledTableCell } from 'components/Table';
import i18n from 'i18n';
import { useStatusesTable } from '../../../hooks/useStatusesTable';
import * as S from '../../../styles';
import { BUTTONS } from '../../../TransportCloudTable.consts';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';
import { ColorPickerPopover } from './ColorPickerPopover';
import { NEW_ROW_ID, type StatusType } from './StatusesTable.types';

type Props = {
  kind: StatusType;
};

export const StatusesTable = ({ kind }: Props) => {
  const {
    rows,
    loading,
    saving,
    selected,
    hoveredId,
    setHoveredId,
    editingId,
    draft,
    errors,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleAdd,
    handleStartEdit,
    handleDraftChange,
    handleToggleEnabled,
    handleConfirmEdit,
    handleCancelEdit,
    handleSelect,
    handleDeleteConfirm,
  } = useStatusesTable(kind);

  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box>
      <S.StatusesToolbar>
        <S.StatusesToolbarTitle>
          {i18n.t(`tradeTypeLabels.${kind}`, { ns: 'activity' }) as string}
        </S.StatusesToolbarTitle>
        <S.TCStyledButton
          $primary
          disabled={editingId !== null || saving}
          onClick={handleAdd}
        >
          {BUTTONS.add}
        </S.TCStyledButton>
        <S.TCStyledButton
          $primary
          disabled={selected.length === 0 || saving}
          onClick={() => setDeleteDialogOpen(true)}
        >
          {BUTTONS.delete}
        </S.TCStyledButton>
      </S.StatusesToolbar>

      <S.StatusesTableWrapper>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 160,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          <TableContainer>
            <Table size='small'>
              <colgroup>
                <col style={{ width: 48 }} />
                <col style={{ width: 64 }} />
                <col />
                <col style={{ width: 72 }} />
              </colgroup>
              <TableBody>
                {rows.map((row) => {
                  const isEditing = editingId === row.id;
                  const isHovered = hoveredId === row.id;
                  const color = isEditing ? draft.color : row.color;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onMouseEnter={() => setHoveredId(row.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      sx={{ opacity: row.enabled ? 1 : 0.5 }}
                    >
                      <TableCell padding='checkbox'>
                        {row.id !== NEW_ROW_ID && (
                          <Checkbox
                            color='primary'
                            size='small'
                            checked={selected.includes(row.id)}
                            onClick={(event) => handleSelect(event, row.id)}
                          />
                        )}
                      </TableCell>
                      <StyledTableCell height='condensed'>
                        {isEditing ? (
                          <S.ColorSelectButton
                            type='button'
                            disabled={saving}
                            onClick={(event) =>
                              setPickerAnchor(event.currentTarget)
                            }
                          >
                            <S.ColorSwatch $color={color} />
                            <ArrowDropDownIcon fontSize='small' />
                          </S.ColorSelectButton>
                        ) : (
                          <S.ColorSwatch $color={color} />
                        )}
                      </StyledTableCell>
                      <StyledTableCell height='condensed'>
                        <S.StatusNameCell>
                          {isEditing ? (
                            <TextField
                              value={draft.name}
                              onChange={(event) =>
                                handleDraftChange('name', event.target.value)
                              }
                              error={errors.name}
                              size='small'
                              variant='outlined'
                              autoFocus
                              disabled={saving}
                              onClick={(event) => event.stopPropagation()}
                              sx={S.driverInputSx}
                            />
                          ) : (
                            <Box
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {row.name}
                            </Box>
                          )}
                          {isEditing && (
                            <S.StatusVisibilityButton
                              type='button'
                              $enabled={draft.enabled}
                              disabled={saving}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleToggleEnabled(row);
                              }}
                            >
                              {draft.enabled ? (
                                <VisibilityIcon fontSize='small' />
                              ) : (
                                <VisibilityOffIcon fontSize='small' />
                              )}
                            </S.StatusVisibilityButton>
                          )}
                        </S.StatusNameCell>
                      </StyledTableCell>
                      <TableCell
                        padding='none'
                        sx={{ width: 72, pr: 0.5, textAlign: 'right' }}
                      >
                        {isEditing ? (
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                          >
                            <IconButton
                              size='small'
                              disabled={saving}
                              onClick={() => handleConfirmEdit(row.id)}
                            >
                              <CheckIcon
                                fontSize='small'
                                sx={{ color: 'action.active' }}
                              />
                            </IconButton>
                            <IconButton
                              size='small'
                              disabled={saving}
                              onClick={() => {
                                setPickerAnchor(null);
                                handleCancelEdit();
                              }}
                            >
                              <CloseIcon
                                fontSize='small'
                                sx={{ color: 'action.active' }}
                              />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size='small'
                            disabled={editingId !== null}
                            sx={{
                              opacity: isHovered && editingId === null ? 1 : 0,
                              transition: 'opacity 0.15s',
                            }}
                            onClick={() => handleStartEdit(row)}
                          >
                            <DriveFileRenameOutlineIcon
                              fontSize='small'
                              sx={{ color: 'action.active' }}
                            />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </S.StatusesTableWrapper>

      {pickerAnchor && (
        <ColorPickerPopover
          anchorEl={pickerAnchor}
          value={draft.color}
          disabled={saving}
          onClose={() => setPickerAnchor(null)}
          onConfirm={(color) => {
            handleDraftChange('color', color);
            setPickerAnchor(null);
          }}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};
